/**
 * I-2439: a signed-in refusal a reader sees, as a code plus its facts.
 *
 * The server words each refusal in English for the CLI and the logs, and carries the same refusal as
 * `code` and `args`, so a screen says it from its catalogue (`failures.readerRefusals.<key>`) in the
 * reader's language instead of showing the deployment's English (F-0324: the reason is data, the
 * sentence is the client's). A client that does not know a code shows the English as it came.
 *
 * The signed-out doors have their own codes (`SIGNED_OUT_REFUSALS`, `credentials.ts`); a refusal that
 * names a role has its own (`role-refusal.ts`), because its facts are the role's names.
 */
export const READER_REFUSALS = {
  /** `AccessControlService.assert`: the subject does not hold a permission where it asked. */
  PERMISSION_MISSING: 'permission-missing',
  WORK_AREA_NOT_FOUND: 'work-area-not-found',
  WORK_ITEM_NOT_FOUND: 'work-item-not-found',
  /** An item asked for by its key (`workItemNotFound`, I-0819); `reference` is the key. */
  WORK_ITEM_KEY_NOT_FOUND: 'work-item-key-not-found',
  WORK_ITEM_RELATION_NOT_FOUND: 'work-item-relation-not-found',
  /** PX-107: an agent's drafted question to a guest role, sent or edited through the comment routes. */
  QUESTION_IS_A_DRAFT: 'question-is-a-draft',
  /** PX-107: a question (or its comment) that is not a draft, sent as one. */
  QUESTION_NOT_A_DRAFT: 'question-not-a-draft',
  // The refusals a guest or a customer reads (I-2439, the customer-facing services).
  /** A request whose shape FEATit does not read; `field` names the part. A screen sends the right shape, so a reader meets it only through a fault: it is said as one, the English kept for an agent. */
  REQUEST_INVALID: 'request-invalid',
  /** A guest asked for an item that is not shared with them, or not there. */
  ITEM_NOT_READABLE: 'item-not-readable',
  /** An agent tried an act only the person may do; `act` is ok, pin, approval or signOff. */
  PERSON_ONLY_ACT: 'person-only-act',
  FILING_TITLE_MISSING: 'filing-title-missing',
  FILING_FILES_MISSING: 'filing-files-missing',
  FILING_TYPE_NOT_FILEABLE: 'filing-type-not-fileable',
  /** `field` is the field key the guest sent. */
  FIELD_NOT_FILLABLE: 'field-not-fillable',
  /** `max` and `sent` are counts. */
  FILES_TOO_MANY: 'files-too-many',
  /** `name` is the file's name and `maxMegabytes` the limit. */
  FILE_TOO_LARGE: 'file-too-large',
  /** `max` is the limit in characters. */
  PIN_TEXT_INVALID: 'pin-text-invalid',
  /** `round` is the round's label. */
  PIN_VERSION_NOT_IN_ROUND: 'pin-version-not-in-round',
  COMMENT_NOT_YOURS: 'comment-not-yours',
  COMMENT_ANSWERED: 'comment-answered',
  REVISION_DIGEST_STALE: 'revision-digest-stale',
  /** `key` is the item; `outcome` is confirmed or declined. */
  APPROVAL_ANSWER_MISSING: 'approval-answer-missing',
  /** `max` is the limit. */
  APPROVE_ALL_TOO_MANY: 'approve-all-too-many',
  /** `key` is the item; `missing` the parts left out, as their keys. */
  PLAN_ANSWER_INCOMPLETE: 'plan-answer-incomplete',
  /** `key` is the item, `what` sign-off or approval, `count` how many wait. */
  ANSWER_REQUEST_AMBIGUOUS: 'answer-request-ambiguous',
  /** `version` is the version the guests read. */
  DESCRIPTION_ALREADY_SHARED: 'description-already-shared',
  /** `role` is the key that was sent. */
  GUEST_ROLE_UNKNOWN: 'guest-role-unknown',
  DELIVERY_NOT_FOUND: 'delivery-not-found',
  DELIVERY_REVOKED: 'delivery-revoked',
  DELIVERY_EXPIRED: 'delivery-expired',
  /** `file` is the file's download name. */
  DELIVERY_FILE_GONE: 'delivery-file-gone',
  /** `kind` is the destination asked for. */
  DELIVERY_KIND_UNSUPPORTED: 'delivery-kind-unsupported',
  DELIVERY_NOTHING_APPROVED: 'delivery-nothing-approved',
  SHARE_NOT_FOUND: 'share-not-found',
  ACCESS_REQUEST_NOT_FOUND: 'access-request-not-found',
  ACCESS_REQUEST_WORK_AREA_MISSING: 'access-request-work-area-missing',
  /** `max` is the limit in characters. */
  ACCESS_REQUEST_REASON_LENGTH: 'access-request-reason-length',
  /** `address` is what was typed. */
  INVITATION_ADDRESS_INVALID: 'invitation-address-invalid',
  INVITATION_NAME_INVALID: 'invitation-name-invalid',
  INVITATION_NAME_WITHOUT_ADDRESS: 'invitation-name-without-address',
  /** `language` is what was asked for. */
  INVITATION_LANGUAGE_UNKNOWN: 'invitation-language-unknown',
  INVITATION_ONE_ADDRESS_NOT_SHARED: 'invitation-one-address-not-shared',
  INVITATION_SINGLE_USE_CAP: 'invitation-single-use-cap',
  INVITATION_NO_PLACE: 'invitation-no-place',
  /** `max` is the limit. */
  INVITATION_TOO_MANY_PLACES: 'invitation-too-many-places',
  INVITATION_PLACE_TWICE: 'invitation-place-twice',
  INVITATION_NO_OWNER_LINK: 'invitation-no-owner-link',
  INVITATION_SHARED_LINK_MANAGER: 'invitation-shared-link-manager',
  /** `organisations` names the organisations, joined. */
  INVITATION_ONE_ORGANISATION: 'invitation-one-organisation',
  INVITATION_LINK_NOT_FOUND: 'invitation-link-not-found',
  INVITATION_PLACE_NOT_ON_LINK: 'invitation-place-not-on-link',
  /** `then` is copy (a person resending) or ask (the invitee renewing). */
  INVITATION_LINK_NO_ADDRESS: 'invitation-link-no-address',
  INVITATION_PLACE_GONE: 'invitation-place-gone',
  INVITATION_CREATOR_CANNOT_GRANT: 'invitation-creator-cannot-grant',
  INVITATION_NOTHING_GRANTABLE: 'invitation-nothing-grantable',
  /** `then` is signUp or signIn. */
  INVITATION_OTHER_ADDRESS: 'invitation-other-address',
  INVITATION_ADDRESS_UNVERIFIED: 'invitation-address-unverified',
  INVITATION_LINKS_MANAGERS_ONLY: 'invitation-links-managers-only',
  INVITATION_SCOPE_NOT_FOUND: 'invitation-scope-not-found',
  /** `min` and `max` are the range; `unit` is days or uses. */
  INVITATION_OUT_OF_RANGE: 'invitation-out-of-range',
  /** F-0815 UTN-2: `min` and `max` are the urgent-task raise window's range, in whole minutes. */
  URGENT_TASK_WINDOW_OUT_OF_RANGE: 'urgent-task-window-out-of-range',
  // IN-110 (slice 4): a person's own settings (`user-preferences.service.ts`).
  /** `max` is the limit. */
  OVERVIEW_VIEWS_TOO_MANY: 'overview-views-too-many',
  /** `max` is the limit. */
  OVERVIEW_BLOCKS_TOO_MANY: 'overview-blocks-too-many',
  /** `max` is the limit. */
  OPENING_TABS_TOO_MANY: 'opening-tabs-too-many',
  /** `max` is the limit. */
  NOTIFICATION_WORK_AREAS_TOO_MANY: 'notification-work-areas-too-many',
  /** `max` is the most the toolbar holds. */
  TOOLBAR_DESTINATIONS_OUT_OF_RANGE: 'toolbar-destinations-out-of-range',
  /** `min` and `max` are the range in minutes. */
  AGENT_IDLE_MINUTES_OUT_OF_RANGE: 'agent-idle-minutes-out-of-range',
  OVERVIEW_VIEW_NAME_MISSING: 'overview-view-name-missing',
  SILENT_END_INVALID: 'silent-end-invalid',
  NOTIFICATION_RULES_TWICE: 'notification-rules-twice',
  PERSON_ONLY_ACCESS_CONFIRMATION: 'person-only-access-confirmation',
  /** `max` is how many favourite views one person keeps. */
  FAVOURITE_VIEWS_TOO_MANY: 'favourite-views-too-many',
  /** `settings` names which of a person's own settings another session saved first. */
  SETTINGS_CHANGED_ELSEWHERE: 'settings-changed-elsewhere',

  // IN-115 (slice 8): the parts tree and an item's parts (`parts.service.ts`).
  PART_NOT_FOUND: 'part-not-found',
  /** `reference` is what was asked for. */
  PART_REFERENCE_NOT_FOUND: 'part-reference-not-found',
  /** `reference` is what was asked for; `nearest` the handles closest to it. */
  PART_REFERENCE_NOT_FOUND_NEAREST: 'part-reference-not-found-nearest',
  /** `name` is the retired part; `act` what reopening it would allow. */
  PART_RETIRED_FIRST: 'part-retired-first',
  /** `name` is the retired part; `into` the part it was merged into. */
  PART_RETIRED_MERGED: 'part-retired-merged',
  PART_TARGET_RETIRED: 'part-target-retired',
  PART_MOVE_LOOP: 'part-move-loop',
  PART_SPLIT_NEEDS_TWO: 'part-split-needs-two',
  PART_MERGE_INTO_ITSELF: 'part-merge-into-itself',
  /** `name` is the part; `into` the one of its own parts it was to be merged into. */
  PART_MERGE_INTO_OWN_PART: 'part-merge-into-own-part',
  PART_MOVE_OPEN_ELSEWHERE: 'part-move-open-elsewhere',
  PART_IN_USE: 'part-in-use',
  PART_RELATION_TO_ITSELF: 'part-relation-to-itself',
  /** `name` is the part; `target` the part it already sits under. */
  PART_ALSO_UNDER_ALREADY: 'part-also-under-already',
  /** `name` is the part; `target` one of its own parts. */
  PART_ALSO_UNDER_LOOP: 'part-also-under-loop',
  /** `names` are the parts `key` only inherits. */
  PART_NOT_OWN: 'part-not-own',
  /** `names` are the retired parts. */
  PART_RETIRED_NOT_GIVEN: 'part-retired-not-given',
  /** `names` are the retired parts; `replacement` the part that replaced the first. */
  PART_RETIRED_REPLACED: 'part-retired-replaced',
  PART_OTHER_WORK_AREA: 'part-other-work-area',
  PART_NAME_MISSING: 'part-name-missing',
  /** `max` is the limit in characters. */
  PART_NAME_TOO_LONG: 'part-name-too-long',
  PART_NAME_TAKEN: 'part-name-taken',
  /** `parent` is the part the name is taken under. */
  PART_NAME_TAKEN_UNDER: 'part-name-taken-under',
  /** `handle` is what was sent; `max` the limit in characters. */
  PART_HANDLE_INVALID: 'part-handle-invalid',
  /** `name` is the part that holds or held `handle`. */
  PART_HANDLE_TAKEN: 'part-handle-taken',
  /** `max` is the limit in characters. */
  PART_SUMMARY_TOO_LONG: 'part-summary-too-long',
  // IN-112 (slice 6): a work area's pages.
  DASHBOARD_PAGE_NOT_FOUND: 'dashboard-page-not-found',
  /** `what` names the page, the pages under a page, or the work area's pages. */
  PAGE_CHANGED_ELSEWHERE: 'page-changed-elsewhere',
  PAGE_PARENT_IS_HOME: 'page-parent-is-home',
  PAGE_PARENT_NOT_IN_WORK_AREA: 'page-parent-not-in-work-area',
  /** `parent`, `max` and the parent's `level`. */
  PAGE_TREE_TOO_DEEP: 'page-tree-too-deep',
  /** `page` is its title. */
  PAGE_MOVE_UNDER_ITSELF: 'page-move-under-itself',
  /** `page` and `parent` are titles. */
  PAGE_MOVE_INTO_OWN_PAGE: 'page-move-into-own-page',
  /** `page`, `parent`, `max`, and the `level` its deepest page would reach. */
  PAGE_MOVE_TOO_DEEP: 'page-move-too-deep',
  HOME_NOT_MOVED: 'home-not-moved',
  HOME_NOT_DELETED: 'home-not-deleted',
  HOME_TAKES_NO_ICON: 'home-takes-no-icon',
  HOME_NOT_SHARED: 'home-not-shared',
  HOME_TAKES_NO_AUDIENCE: 'home-takes-no-audience',
  HOME_NOT_FOR_ONE_ITEM: 'home-not-for-one-item',
  /** `page` is its title, `count` how many pages are under it. */
  PAGE_HAS_PAGES_UNDER: 'page-has-pages-under',
  /** `role` is the key asked for. */
  PREVIEW_ROLE_UNKNOWN: 'preview-role-unknown',
  /** `roles` lists the keys not found. */
  SHARE_ROLES_UNKNOWN: 'share-roles-unknown',
  SHARE_USER_UNKNOWN: 'share-user-unknown',
  /** `permission` is the one asked for. */
  AUDIENCE_PERMISSION_UNKNOWN: 'audience-permission-unknown',
  /** `key` is the item type asked for. */
  PAGE_ITEM_TYPE_UNKNOWN: 'page-item-type-unknown',
  /** `max` is the page's block limit. */
  PAGE_BLOCKS_TOO_MANY: 'page-blocks-too-many',
  PAGE_TITLE_MISSING: 'page-title-missing',
  /** `icon` is the name asked for. */
  PAGE_ICON_UNKNOWN: 'page-icon-unknown',
  READER_RESIZE_NOT_OFFERED: 'reader-resize-not-offered',
  LIST_OPENS_ON_ITEM_PAGE_ONLY: 'list-opens-on-item-page-only',
  PAGE_DOCUMENTS_OTHER_WORK_AREA: 'page-documents-other-work-area',
  PAGE_VIEWS_NOT_SHARED: 'page-views-not-shared',
  /** `fields` lists the names no field template defines. */
  ITEM_FIELDS_UNDEFINED: 'item-fields-undefined',
  /** `max` is the block's field limit. */
  ITEM_FIELDS_TOO_MANY: 'item-fields-too-many',
  /** `role` is the one asked for. */
  APPROVAL_ROLE_UNKNOWN: 'approval-role-unknown',
  // IN-118: a person's credentials and sign-in.
  CREDENTIAL_NOT_FOUND: 'credential-not-found',
  /** `act` names what only a signed-in session may do. */
  SIGNED_IN_SESSION_ONLY: 'signed-in-session-only',
  AGENT_CREDENTIAL_OVER_CEILING: 'agent-credential-over-ceiling',
  AGENT_CREDENTIAL_NOT_YOURS: 'agent-credential-not-yours',
  MAC_CONSENT_OWN_SIGN_IN: 'mac-consent-own-sign-in',
  /** `action` is what it tried on its own credential. */
  VERIFICATION_SESSION_OWN_CREDENTIAL: 'verification-session-own-credential',
  TERMS_PERSON_ONLY: 'terms-person-only',
  /** F-0817 CAL-1: the Critical Alerts switch was sent as something other than true or false. */
  CRITICAL_ALERTS_NOT_A_SWITCH: 'critical-alerts-not-a-switch',
  // MJ-6 (T-0919): the make route's refusals (`ItemMakingService.make`), said the same to the web,
  // `featit make` and MCP.
  /** `key` is the item; its type names no item it makes. */
  MAKES_NOTHING: 'makes-nothing',
  /** `reason` is the package's `notYet` in the caller's language; `status` is its `requiresState`. */
  MAKE_NOT_YET: 'make-not-yet',
  /** `key` is the item; `fields` names the fields `makes.each` reads, none of which holds a value. */
  MAKE_NOTHING_NAMED: 'make-nothing-named',
  /**
   * I-2521 (CF-30), I-2439 (IN-101): a required field the filer was offered is empty, so nothing was
   * filed. `label` is the field's own label as the template keeps it: a package text (`{ en, de }`),
   * said in the page's language ({@link readerRefusalOf}), or one string, said as it is.
   */
  FIELD_REQUIRED: 'field-required',
  /** The one-tap door's own codes (`guest-entry.service.ts`), as it already sends them. */
  SIGN_IN_LINK_GONE: 'sign_in_link_gone',
  GUEST_ADDRESS_REQUIRED: 'email_required',
  INVITATION_NEEDS_SIGN_IN: 'invitation_needs_sign_in',
  // I-2439 slice 2 (IN-106): what a customer meets on a delivery, a question or an item they sent.
  /** A delivery that is not there, not sent yet, or not the reader's: one answer (AGM-26). */
  DELIVERY_NOT_READABLE: 'delivery-not-readable',
  /** A question that is not there, or not the guest's to answer. */
  GUEST_QUESTION_NOT_FOUND: 'guest-question-not-found',
  /** An item the reader did not send here, or may no longer comment on. */
  SENT_ITEM_NOT_FOUND: 'sent-item-not-found',
  COMMENT_EMPTY: 'comment-empty',
  /** IN-106a: `key` is the flow's step, `role` the role it waits for, which the member does not hold. */
  APPROVAL_ROLE_NOT_HELD: 'approval-role-not-held',
  // IN-106b: changing one's own name, contact address or sign-in address.
  PERSON_ONLY_NAME_CHANGE: 'person-only-name-change',
  PERSON_ONLY_ADDRESS_CHANGE: 'person-only-address-change',
  OWN_NAME_MISSING: 'own-name-missing',
  /** `max` is the limit in characters. */
  OWN_NAME_TOO_LONG: 'own-name-too-long',
  OWN_ACCOUNT_NOT_FOUND: 'own-account-not-found',
  EMAIL_ADDRESS_INVALID: 'email-address-invalid',
  SIGN_IN_ADDRESS_SAME: 'sign-in-address-same',
  SIGN_IN_ADDRESS_CHANGES_TOO_MANY: 'sign-in-address-changes-too-many',
  // IN-106d (slice 2b).
  /** `max` is how many comments an item may have pinned. */
  COMMENT_PINS_TOO_MANY: 'comment-pins-too-many',
  /** `max` is the limit in characters. */
  REVIEW_LINK_NAME_MISSING: 'review-link-name-missing',
  REVIEW_LINK_ADDRESS_MISSING: 'review-link-address-missing',
  REVIEW_LINK_ROLE_NOT_GUEST: 'review-link-role-not-guest',
  /** `key` is the item. */
  REVIEW_LINK_ROUND_NOT_FOUND: 'review-link-round-not-found',
  /** `round` is the round the link was asked for, `key` the item: a newer round is its current one. */
  REVIEW_LINK_ROUND_STALE: 'review-link-round-stale',
  REVIEW_LINK_NOT_FOUND: 'review-link-not-found',
  PASSKEY_REGISTRATION_NOT_VERIFIED: 'passkey-registration-not-verified',
  PASSKEY_ALREADY_REGISTERED: 'passkey-already-registered',
  PASSKEY_CHALLENGE_EXPIRED: 'passkey-challenge-expired',
  PASSKEY_NOT_FOUND: 'passkey-not-found',
  /** A deployment link code used, expired, unknown or wrong: one answer (PIO-31a). */
  DEPLOYMENT_LINK_CODE_SPENT: 'deployment-link-code-spent',
  // IN-108 (slice 3): a member's item refusals (`work-items.service.ts`).
  ITEM_RELATES_TO_ITSELF: 'item-relates-to-itself',
  ITEM_OWN_PARENT: 'item-own-parent',
  ITEM_UNDER_OWN_DESCENDANT: 'item-under-own-descendant',
  ITEM_PARENT_OTHER_WORK_AREA: 'item-parent-other-work-area',
  ITEM_MERGED_INTO_ITSELF: 'item-merged-into-itself',
  RESPONSIBLE_USER_MISSING: 'responsible-user-missing',
  RESPONSIBLE_USER_NOT_FOUND: 'responsible-user-not-found',
  RESPONSIBLE_AGENT_MISSING: 'responsible-agent-missing',
  RESPONSIBLE_AGENT_NOT_FOUND: 'responsible-agent-not-found',
  /** `agent` is its name, `status` its state. */
  RESPONSIBLE_AGENT_NOT_IN_SERVICE: 'responsible-agent-not-in-service',
  ITEM_DESCRIPTION_REQUIRED: 'item-description-required',
  WORKFLOW_STATE_UNKNOWN: 'workflow-state-unknown',
  REPOSITION_REASON_MISSING: 'reposition-reason-missing',
  /** `key` is the item. */
  ITEM_CHANGED_SINCE_READ: 'item-changed-since-read',
  /** `key` is the item. */
  MOVE_NOT_WAITING: 'move-not-waiting',
  WORK_AREA_STATUSES_MISSING: 'work-area-statuses-missing',
  WORK_AREA_STATUS_KEY_EMPTY: 'work-area-status-key-empty',
  /** `key` is the status key named twice. */
  WORK_AREA_STATUS_KEY_TWICE: 'work-area-status-key-twice',
  /** `key` is the status. */
  WORK_AREA_STATUS_NAME_MISSING: 'work-area-status-name-missing',
  ITEM_TYPE_UNKNOWN: 'item-type-unknown',
  // F-0794: a published package's refusals.
  /** `key` and `version` are the version asked to be withdrawn. */
  PACKAGE_VERSION_NOT_PUBLISHED: 'package-version-not-published',
  /** `key` is a package that ships with the server, so the server's own upgrade moves it. */
  PACKAGE_SHIPS_WITH_SERVER: 'package-ships-with-server',
  /** `key` is the published package asked for. */
  PACKAGE_NOT_INSTALLED: 'package-not-installed',
  /** `key`: no published package of that key that this caller may see (unknown and unseen alike). */
  PACKAGE_NOT_PUBLISHED: 'package-not-published',
  /** `organisations`: the paths named that are no organisation, joined. */
  ORGANISATIONS_UNKNOWN: 'organisations-unknown',
  /** `key` and `version`: a copy from a connected deployment whose file no longer has its digest. */
  PACKAGE_LINKED_COPY_CHANGED: 'package-linked-copy-changed',
  // F-0794 S8b: the system admin's links to other deployments, said in the admin console.
  /** `variable` is the setting that names this deployment's public address. */
  DEPLOYMENT_LINK_ORIGIN_UNSET: 'deployment-link-origin-unset',
  DEPLOYMENT_LINK_CODE_INVALID: 'deployment-link-code-invalid',
  DEPLOYMENT_LINK_NOT_FOUND: 'deployment-link-not-found',
  /** `state` is the link's state: invited, pending or revoked. */
  DEPLOYMENT_LINK_NOT_ACTIVE: 'deployment-link-not-active',
  // F-0794 S8g (I-2558): an upgrade by hand that merges a page.
  /** `key` and `name` are the page's: this version applies to it as it is, so there is nothing to merge. */
  PACKAGE_MERGE_NOTHING: 'package-merge-nothing',
  /** `name` is the page; `refs` the changes answered wrong: not answered, not listed, or a side not offered. */
  PACKAGE_MERGE_INCOMPLETE: 'package-merge-incomplete',
  /** `name` is the page, changed since the preview read it. */
  PACKAGE_PAGE_CHANGED_SINCE_PREVIEW: 'package-page-changed-since-preview',
  /** `refs`: what a merge named that is no page an earlier version made. */
  PACKAGE_MERGE_NOT_A_PAGE: 'package-merge-not-a-page',
  // F-0809: a deployment invitation's refusals, as `DeploymentInvitationsService` sends them.
  /** The item is not a confirmed early-access request (another work area, unconfirmed or purged). */
  DEPLOYMENT_INVITATION_NOT_A_REQUEST: 'deployment-invitation-not-a-request',
  /** The request or its address has an invitation still open. Send it again instead. */
  DEPLOYMENT_INVITATION_OPEN: 'deployment-invitation-open',
  /** There is no open invitation to send again. */
  DEPLOYMENT_INVITATION_NONE_OPEN: 'deployment-invitation-none-open',
  /** The address already has an account here; they sign in. */
  DEPLOYMENT_INVITATION_ACCOUNT_EXISTS: 'deployment-invitation-account-exists',
  // IN-117 (slice 10): the owner flow (`owner-flow.service.ts`).
  OWNER_FLOW_IDEA_NOT_FOUND: 'owner-flow-idea-not-found',
  /** `key` is the item. */
  OWNER_FLOW_NOT_AN_IDEA: 'owner-flow-not-an-idea',
  /** `name` is the owner, `key` the idea. */
  OWNER_FLOW_ALREADY_OWNS: 'owner-flow-already-owns',
  OWNER_FLOW_NOT_AN_OWNER: 'owner-flow-not-an-owner',
  /** `name` is the person named. */
  OWNER_FLOW_OWNER_ROLE_MISSING: 'owner-flow-owner-role-missing',
  /** `name` is the person named. */
  OWNER_FLOW_LEAD_ROLE_MISSING: 'owner-flow-lead-role-missing',
  /** `name` is the person named. */
  OWNER_FLOW_WRITER_ROLE_MISSING: 'owner-flow-writer-role-missing',
  /** `name` is the person named. */
  OWNER_FLOW_CANNOT_BE_TOLD: 'owner-flow-cannot-be-told',
  OWNER_FLOW_WRITER_AGENT_REFUSED: 'owner-flow-writer-agent-refused',
  OWNER_FLOW_INTAKE_OWNER_REFUSED: 'owner-flow-intake-owner-refused',
  OWNER_FLOW_PERSON_NOT_FOUND: 'owner-flow-person-not-found',
  OWNER_FLOW_ROLE_NOT_FOUND: 'owner-flow-role-not-found',
  OWNER_FLOW_AGENT_NOT_FOUND: 'owner-flow-agent-not-found',
  OWNER_FLOW_INTAKE_UNCHANGEABLE: 'owner-flow-intake-unchangeable',
  /** The agent holds a role here its owner's access no longer covers. */
  RESPONSIBLE_AGENT_OUT_OF_REACH: 'responsible-agent-out-of-reach',
  /** The item has no legacy source to record. */
  ITEM_NOT_IMPORTED: 'item-not-imported',
  /** A removed status still holds items. */
  WORK_AREA_STATUS_IN_USE: 'work-area-status-in-use',
  /** No next-up configuration by that key. */
  NEXT_UP_CONFIG_NOT_FOUND: 'next-up-config-not-found',
  /** The item has no status to reopen to. */
  ITEM_NO_REOPEN_TARGET: 'item-no-reopen-target',
  /** The move waits for the item's checks. */
  ITEM_CHECKS_WAITING: 'item-checks-waiting',
  /** The item waits at parallel nodes and the answer names none. */
  FLOW_ANSWER_NAMES_NO_NODE: 'flow-answer-names-no-node',
  /** The node does not offer that output. */
  FLOW_ANSWER_NOT_OFFERED: 'flow-answer-not-offered',
  /** Each approver has a request of their own, and the answer names none. */
  FLOW_ANSWER_NAMES_NO_APPROVER: 'flow-answer-names-no-approver',
  /** An OK to a plan names the values shown, not a revision. */
  FLOW_PLAN_NOT_REVISION: 'flow-plan-not-revision',
  /** The approver's request was answered already. */
  FLOW_REQUEST_ANSWERED: 'flow-request-answered',
  /** The node moves on by itself when the children are done. */
  FLOW_WAITS_FOR_CHILDREN: 'flow-waits-for-children',
  /** A generated flow moves by status and has no nodes. */
  FLOW_GENERATED_NO_NODES: 'flow-generated-no-nodes',
  /** The item already waits for a person to confirm. */
  ITEM_AWAITS_CONFIRMATION: 'item-awaits-confirmation',
  /** The person a step is handed to does not exist. */
  STEP_PERSON_UNKNOWN: 'step-person-unknown',
  /** The recipient of a hand-over cannot take the items. */
  ITEM_HAND_OVER_REFUSED: 'item-hand-over-refused',
  /** A type only its own work area defines cannot leave it. */
  ITEM_TYPE_STAYS_IN_WORK_AREA: 'item-type-stays-in-work-area',
  /** Evidence links are http(s) addresses. */
  EVIDENCE_LINK_INVALID: 'evidence-link-invalid',
  /** An evidence file is an attachment of the item. */
  EVIDENCE_FILE_NOT_ATTACHMENT: 'evidence-file-not-attachment',
  /** The work area has no item flow by that key. */
  ITEM_FLOW_NOT_FOUND: 'item-flow-not-found',
  /** A flow switch names no items. */
  FLOW_SWITCH_NOTHING_NAMED: 'flow-switch-nothing-named',
  /** A flow switch names too many items. */
  FLOW_SWITCH_TOO_MANY: 'flow-switch-too-many',
  /** A flow switch stays in one work area. */
  FLOW_SWITCH_OTHER_WORK_AREA: 'flow-switch-other-work-area',
  /** A type that moves by its own service only. */
  ITEM_MOVES_BY_OWN_SERVICE: 'item-moves-by-own-service',
  /** The item's parts changed since they were read. */
  ITEM_PARTS_CHANGED: 'item-parts-changed',
  /** Only items in one work area merge. */
  ITEM_MERGE_OTHER_WORK_AREA: 'item-merge-other-work-area',
  /** The work area has no cancelled status to close a merged request in. */
  ITEM_MERGE_NO_CANCELLED_STATUS: 'item-merge-no-cancelled-status',
  /** Related items stay in one work area, a decision's links aside. */
  ITEM_RELATED_OTHER_WORK_AREA: 'item-related-other-work-area',
  /** No state to release a confirmed item into. */
  ITEM_CONFIRM_NO_STATE: 'item-confirm-no-state',
  /** The in-progress cap's setting cannot be read. */
  WORK_AREA_CAP_UNREADABLE: 'work-area-cap-unreadable',
  /** The in-progress cap has no status to count. */
  WORK_AREA_CAP_NO_STATUS: 'work-area-cap-no-status',
  /** The landed-commit rule is on without its repository and branch. */
  RESOLVED_SHA_NOT_CONFIGURED: 'resolved-sha-not-configured',
  /** The move needs a landed commit on the item. */
  LANDED_SHA_MISSING: 'landed-sha-missing',
  /** The landed commit is not a valid SHA. */
  LANDED_SHA_MALFORMED: 'landed-sha-malformed',
  /** The landed commit cannot be verified against the branch. */
  LANDED_SHA_NOT_VERIFIABLE: 'landed-sha-not-verifiable',
  /** The record of commits could not be read. */
  LANDED_SHA_RECORD_UNREADABLE: 'landed-sha-record-unreadable',
  /** The landed commit starts several recorded commits. */
  LANDED_SHA_AMBIGUOUS: 'landed-sha-ambiguous',
  /** No commit recorded in the work area starts with it. */
  LANDED_SHA_NOT_RECORDED: 'landed-sha-not-recorded',
  /** An agent may not take a person's decision. */
  AGENT_TAKES_PERSONS_DECISION: 'agent-takes-persons-decision',
  /** The address cannot be part of a link to a work area. */
  WORK_AREA_ADDRESS_UNUSABLE: 'work-area-address-unusable',
  /** The namespace already has a work area at this address. */
  WORK_AREA_ADDRESS_TAKEN: 'work-area-address-taken',
  /** The address was given up recently and is still held for its work area. */
  WORK_AREA_ADDRESS_HELD: 'work-area-address-held',
  /** The organisation or group a visibility setting names is gone. */
  PUBLIC_PLACE_NOT_FOUND: 'public-place-not-found',
  /** A group would be more visible than its organisation. */
  GROUP_MORE_VISIBLE_THAN_ORGANISATION: 'group-more-visible-than-organisation',
  /** Public work areas or places below still depend on this one. */
  PUBLIC_PLACES_DEPEND: 'public-places-depend',
  /** A child would be more visible than this work area. */
  WORK_AREA_CHILDREN_MORE_VISIBLE: 'work-area-children-more-visible',
  /** The work area would be more visible than the one it is nested below. */
  WORK_AREA_PARENT_LESS_VISIBLE: 'work-area-parent-less-visible',
  /** Only a Public work area can be offered to search engines. */
  SEARCH_INDEXING_NEEDS_PUBLIC: 'search-indexing-needs-public',
  /** This deployment does not allow search engines to index work areas. */
  SEARCH_INDEXING_NOT_ALLOWED: 'search-indexing-not-allowed',
  /** The person named to answer new requests may not own work here. */
  INTAKE_RESPONSIBLE_REFUSED: 'intake-responsible-refused',
  /** The agent named as responsible may not own work here. */
  AGENT_RESPONSIBLE_REFUSED: 'agent-responsible-refused',
  /** The person has no agent with this id. */
  OWN_AGENT_NOT_FOUND: 'own-agent-not-found',
  /** The agent is not in service, so it cannot be activated in a work area. */
  WORK_AREA_AGENT_NOT_IN_SERVICE: 'work-area-agent-not-in-service',
  /** The default work-area type must stay active. */
  WORK_AREA_TYPE_DEFAULT_INACTIVE: 'work-area-type-default-inactive',
  /** The work-area type is gone. */
  WORK_AREA_TYPE_NOT_FOUND: 'work-area-type-not-found',
  /** This type is the default: another has to become the default first. */
  WORK_AREA_TYPE_IS_DEFAULT: 'work-area-type-is-default',
  /** Work areas use this type, so it cannot be deleted. */
  WORK_AREA_TYPE_IN_USE: 'work-area-type-in-use',
  /** New work areas cannot use an inactive type. */
  WORK_AREA_TYPE_INACTIVE: 'work-area-type-inactive',
  /** Only the owner transfers a work area. */
  TRANSFER_OWNER_ONLY: 'transfer-owner-only',
  /** The person named already owns the work area. */
  TRANSFER_ALREADY_OWNER: 'transfer-already-owner',
  /** The new owner has to be a member first. */
  TRANSFER_NEEDS_MEMBER: 'transfer-needs-member',
  /** The person has no account on this deployment. */
  PERSON_NO_ACCOUNT: 'person-no-account',
  /** The person is already a member of this work area. */
  ALREADY_A_MEMBER: 'already-a-member',
  /** The member is gone from this work area. */
  WORK_AREA_MEMBER_NOT_FOUND: 'work-area-member-not-found',
  /** The owner role of a work area does not change by editing it. */
  OWNER_ROLE_FIXED: 'owner-role-fixed',
  /** The owner cannot be removed from a work area. */
  OWNER_NOT_REMOVABLE: 'owner-not-removable',
  /** This server cannot hand items on. */
  HAND_OVER_UNAVAILABLE: 'hand-over-unavailable',
  /** Some items are not held by someone who can no longer own work here. */
  HAND_OVER_ITEMS_NOT_HELD: 'hand-over-items-not-held',
  /** Items cannot go to the person being removed. */
  HAND_OVER_TO_REMOVED: 'hand-over-to-removed',
  /** A work area cannot be its own parent. */
  WORK_AREA_OWN_PARENT: 'work-area-own-parent',
  /** The parent chosen would make the hierarchy a circle. */
  WORK_AREA_HIERARCHY_CYCLE: 'work-area-hierarchy-cycle',
  /** F-0819: the desktop app's sign-in request holds no valid challenge. */
  DESKTOP_SIGN_IN_REQUEST_INVALID: 'desktop-sign-in-request-invalid',
  // IN-116 (slice 9): what a person or a session meets about an agent (`agents.service.ts`).
  AGENT_NOT_FOUND: 'agent-not-found',
  AGENT_KEY_MISSING: 'agent-key-missing',
  AGENT_NAME_MISSING: 'agent-name-missing',
  /** `key` is what was given. */
  AGENT_KEY_IS_IDENTIFIER: 'agent-key-is-identifier',
  /** `key` is the key asked for. */
  AGENT_KEY_TAKEN: 'agent-key-taken',
  /** `key` is what was asked for, `count` how many agents it matches. */
  AGENT_KEY_AMBIGUOUS: 'agent-key-ambiguous',
  AGENT_ACCESS_UNAVAILABLE: 'agent-access-unavailable',
  /** `key` is the agent merged away. */
  AGENT_MERGED_AWAY: 'agent-merged-away',
  /** `key` is the agent, `status` its state. */
  AGENT_SESSION_OUT_OF_SERVICE: 'agent-session-out-of-service',
  /** `agent` is its name. */
  AGENT_WRITE_OUT_OF_SERVICE: 'agent-write-out-of-service',
  /** `key` is the agent asked for. */
  AGENT_CREATION_OFF: 'agent-creation-off',
  /** `key` is the agent asked for. */
  AGENT_CREATION_NEEDS_APPROVAL: 'agent-creation-needs-approval',
  /** `key` is the agent asked for. */
  AGENT_CREATE_NEEDS_PERMISSION: 'agent-create-needs-permission',
  AGENT_REQUEST_NOT_FOUND: 'agent-request-not-found',
  /** `status` is what the request was answered with. */
  AGENT_REQUEST_ALREADY_DECIDED: 'agent-request-already-decided',
  AGENT_REQUEST_REASON_MISSING: 'agent-request-reason-missing',
  /** `max` is the limit. */
  AGENT_REQUEST_REASON_TOO_LONG: 'agent-request-reason-too-long',
  AGENT_HANDOVER_UNAVAILABLE: 'agent-handover-unavailable',
  AGENT_HANDOVER_TO_ITSELF: 'agent-handover-to-itself',
  AGENT_MERGE_SAME: 'agent-merge-same',
  AGENT_MERGE_TARGET_NOT_ACTIVE: 'agent-merge-target-not-active',
  AGENT_OVERRIDE_NOT_A_COUNT: 'agent-override-not-a-count',
  AGENT_OVERRIDE_NEEDS_DATE: 'agent-override-needs-date',
  /** `date` is what was given. */
  AGENT_OVERRIDE_DATE_INVALID: 'agent-override-date-invalid',
  /** `date` is the date given. */
  AGENT_OVERRIDE_DATE_PAST: 'agent-override-date-past',
  CREDENTIAL_NAMES_ITS_AGENT: 'credential-names-its-agent',
  VIEW_ACT_NOT_ATTRIBUTABLE: 'view-act-not-attributable',
  VIEW_WRITES_AS_YOU: 'view-writes-as-you',
  /** `subagent` and `agent` are their names, `permission` what the role does not give. */
  SUBAGENT_ROLE_REFUSES: 'subagent-role-refuses',
  /** `subagent` and `agent` are their names. */
  SUBAGENT_FILES_NO_FINISHED_ITEM: 'subagent-files-no-finished-item',
  /** `reference` is the fold asked for. */
  SUBAGENT_FOLD_NOT_FOUND: 'subagent-fold-not-found',
} as const;

export type ReaderRefusalCodeType = (typeof READER_REFUSALS)[keyof typeof READER_REFUSALS];

/**
 * Each code's catalogue key under `failures.readerRefusals`. One record, so a code without its
 * sentence is a type error here and a missing translation is `reader-refusal.guard`'s red.
 */
export const READER_REFUSAL_KEYS = {
  'permission-missing': 'permissionMissing',
  'work-area-not-found': 'workAreaNotFound',
  'work-item-not-found': 'workItemNotFound',
  'work-item-key-not-found': 'workItemKeyNotFound',
  'work-item-relation-not-found': 'workItemRelationNotFound',
  'question-is-a-draft': 'questionIsADraft',
  'question-not-a-draft': 'questionNotADraft',
  'request-invalid': 'requestInvalid',
  'item-not-readable': 'itemNotReadable',
  'person-only-act': 'personOnlyAct',
  'filing-title-missing': 'filingTitleMissing',
  'filing-files-missing': 'filingFilesMissing',
  'filing-type-not-fileable': 'filingTypeNotFileable',
  'field-not-fillable': 'fieldNotFillable',
  'files-too-many': 'filesTooMany',
  'file-too-large': 'fileTooLarge',
  'pin-text-invalid': 'pinTextInvalid',
  'pin-version-not-in-round': 'pinVersionNotInRound',
  'comment-not-yours': 'commentNotYours',
  'comment-answered': 'commentAnswered',
  'revision-digest-stale': 'revisionDigestStale',
  'approval-answer-missing': 'approvalAnswerMissing',
  'approve-all-too-many': 'approveAllTooMany',
  'plan-answer-incomplete': 'planAnswerIncomplete',
  'answer-request-ambiguous': 'answerRequestAmbiguous',
  'description-already-shared': 'descriptionAlreadyShared',
  'guest-role-unknown': 'guestRoleUnknown',
  'delivery-not-found': 'deliveryNotFound',
  'delivery-revoked': 'deliveryRevoked',
  'delivery-expired': 'deliveryExpired',
  'delivery-file-gone': 'deliveryFileGone',
  'delivery-kind-unsupported': 'deliveryKindUnsupported',
  'delivery-nothing-approved': 'deliveryNothingApproved',
  'share-not-found': 'shareNotFound',
  'access-request-not-found': 'accessRequestNotFound',
  'access-request-work-area-missing': 'accessRequestWorkAreaMissing',
  'access-request-reason-length': 'accessRequestReasonLength',
  'invitation-address-invalid': 'invitationAddressInvalid',
  'invitation-name-invalid': 'invitationNameInvalid',
  'invitation-name-without-address': 'invitationNameWithoutAddress',
  'invitation-language-unknown': 'invitationLanguageUnknown',
  'invitation-one-address-not-shared': 'invitationOneAddressNotShared',
  'invitation-single-use-cap': 'invitationSingleUseCap',
  'invitation-no-place': 'invitationNoPlace',
  'invitation-too-many-places': 'invitationTooManyPlaces',
  'invitation-place-twice': 'invitationPlaceTwice',
  'invitation-no-owner-link': 'invitationNoOwnerLink',
  'invitation-shared-link-manager': 'invitationSharedLinkManager',
  'invitation-one-organisation': 'invitationOneOrganisation',
  'invitation-link-not-found': 'invitationLinkNotFound',
  'invitation-place-not-on-link': 'invitationPlaceNotOnLink',
  'invitation-link-no-address': 'invitationLinkNoAddress',
  'invitation-place-gone': 'invitationPlaceGone',
  'invitation-creator-cannot-grant': 'invitationCreatorCannotGrant',
  'invitation-nothing-grantable': 'invitationNothingGrantable',
  'invitation-other-address': 'invitationOtherAddress',
  'invitation-address-unverified': 'invitationAddressUnverified',
  'invitation-links-managers-only': 'invitationLinksManagersOnly',
  'invitation-scope-not-found': 'invitationScopeNotFound',
  'invitation-out-of-range': 'invitationOutOfRange',
  'urgent-task-window-out-of-range': 'urgentTaskWindowOutOfRange',
  'overview-views-too-many': 'overviewViewsTooMany',
  'overview-blocks-too-many': 'overviewBlocksTooMany',
  'opening-tabs-too-many': 'openingTabsTooMany',
  'notification-work-areas-too-many': 'notificationWorkAreasTooMany',
  'toolbar-destinations-out-of-range': 'toolbarDestinationsOutOfRange',
  'agent-idle-minutes-out-of-range': 'agentIdleMinutesOutOfRange',
  'overview-view-name-missing': 'overviewViewNameMissing',
  'silent-end-invalid': 'silentEndInvalid',
  'notification-rules-twice': 'notificationRulesTwice',
  'person-only-access-confirmation': 'personOnlyAccessConfirmation',
  'favourite-views-too-many': 'favouriteViewsTooMany',
  'settings-changed-elsewhere': 'settingsChangedElsewhere',
  'part-not-found': 'partNotFound',
  'part-reference-not-found': 'partReferenceNotFound',
  'part-reference-not-found-nearest': 'partReferenceNotFoundNearest',
  'part-retired-first': 'partRetiredFirst',
  'part-retired-merged': 'partRetiredMerged',
  'part-target-retired': 'partTargetRetired',
  'part-move-loop': 'partMoveLoop',
  'part-split-needs-two': 'partSplitNeedsTwo',
  'part-merge-into-itself': 'partMergeIntoItself',
  'part-merge-into-own-part': 'partMergeIntoOwnPart',
  'part-move-open-elsewhere': 'partMoveOpenElsewhere',
  'part-in-use': 'partInUse',
  'part-relation-to-itself': 'partRelationToItself',
  'part-also-under-already': 'partAlsoUnderAlready',
  'part-also-under-loop': 'partAlsoUnderLoop',
  'part-not-own': 'partNotOwn',
  'part-retired-not-given': 'partRetiredNotGiven',
  'part-retired-replaced': 'partRetiredReplaced',
  'part-other-work-area': 'partOtherWorkArea',
  'part-name-missing': 'partNameMissing',
  'part-name-too-long': 'partNameTooLong',
  'part-name-taken': 'partNameTaken',
  'part-name-taken-under': 'partNameTakenUnder',
  'part-handle-invalid': 'partHandleInvalid',
  'part-handle-taken': 'partHandleTaken',
  'part-summary-too-long': 'partSummaryTooLong',
  'dashboard-page-not-found': 'dashboardPageNotFound',
  'page-changed-elsewhere': 'pageChangedElsewhere',
  'page-parent-is-home': 'pageParentIsHome',
  'page-parent-not-in-work-area': 'pageParentNotInWorkArea',
  'page-tree-too-deep': 'pageTreeTooDeep',
  'page-move-under-itself': 'pageMoveUnderItself',
  'page-move-into-own-page': 'pageMoveIntoOwnPage',
  'page-move-too-deep': 'pageMoveTooDeep',
  'home-not-moved': 'homeNotMoved',
  'home-not-deleted': 'homeNotDeleted',
  'home-takes-no-icon': 'homeTakesNoIcon',
  'home-not-shared': 'homeNotShared',
  'home-takes-no-audience': 'homeTakesNoAudience',
  'home-not-for-one-item': 'homeNotForOneItem',
  'page-has-pages-under': 'pageHasPagesUnder',
  'preview-role-unknown': 'previewRoleUnknown',
  'share-roles-unknown': 'shareRolesUnknown',
  'share-user-unknown': 'shareUserUnknown',
  'audience-permission-unknown': 'audiencePermissionUnknown',
  'page-item-type-unknown': 'pageItemTypeUnknown',
  'page-blocks-too-many': 'pageBlocksTooMany',
  'page-title-missing': 'pageTitleMissing',
  'page-icon-unknown': 'pageIconUnknown',
  'reader-resize-not-offered': 'readerResizeNotOffered',
  'list-opens-on-item-page-only': 'listOpensOnItemPageOnly',
  'page-documents-other-work-area': 'pageDocumentsOtherWorkArea',
  'page-views-not-shared': 'pageViewsNotShared',
  'item-fields-undefined': 'itemFieldsUndefined',
  'item-fields-too-many': 'itemFieldsTooMany',
  'approval-role-unknown': 'approvalRoleUnknown',
  'credential-not-found': 'credentialNotFound',
  'signed-in-session-only': 'signedInSessionOnly',
  'agent-credential-over-ceiling': 'agentCredentialOverCeiling',
  'agent-credential-not-yours': 'agentCredentialNotYours',
  'mac-consent-own-sign-in': 'macConsentOwnSignIn',
  'verification-session-own-credential': 'verificationSessionOwnCredential',
  'terms-person-only': 'termsPersonOnly',
  'critical-alerts-not-a-switch': 'criticalAlertsNotASwitch',
  'makes-nothing': 'makesNothing',
  'make-not-yet': 'makeNotYet',
  'make-nothing-named': 'makeNothingNamed',
  'field-required': 'fieldRequired',
  'delivery-not-readable': 'deliveryNotReadable',
  'guest-question-not-found': 'guestQuestionNotFound',
  'sent-item-not-found': 'sentItemNotFound',
  'comment-empty': 'commentEmpty',
  'approval-role-not-held': 'approvalRoleNotHeld',
  'person-only-name-change': 'personOnlyNameChange',
  'person-only-address-change': 'personOnlyAddressChange',
  'own-name-missing': 'ownNameMissing',
  'own-name-too-long': 'ownNameTooLong',
  'own-account-not-found': 'ownAccountNotFound',
  'email-address-invalid': 'emailAddressInvalid',
  'sign-in-address-same': 'signInAddressSame',
  'sign-in-address-changes-too-many': 'signInAddressChangesTooMany',
  'comment-pins-too-many': 'commentPinsTooMany',
  'review-link-name-missing': 'reviewLinkNameMissing',
  'review-link-address-missing': 'reviewLinkAddressMissing',
  'review-link-role-not-guest': 'reviewLinkRoleNotGuest',
  'review-link-round-not-found': 'reviewLinkRoundNotFound',
  'review-link-round-stale': 'reviewLinkRoundStale',
  'review-link-not-found': 'reviewLinkNotFound',
  'passkey-registration-not-verified': 'passkeyRegistrationNotVerified',
  'passkey-already-registered': 'passkeyAlreadyRegistered',
  'passkey-challenge-expired': 'passkeyChallengeExpired',
  'passkey-not-found': 'passkeyNotFound',
  'deployment-link-code-spent': 'deploymentLinkCodeSpent',
  'item-relates-to-itself': 'itemRelatesToItself',
  'item-own-parent': 'itemOwnParent',
  'item-under-own-descendant': 'itemUnderOwnDescendant',
  'item-parent-other-work-area': 'itemParentOtherWorkArea',
  'item-merged-into-itself': 'itemMergedIntoItself',
  'responsible-user-missing': 'responsibleUserMissing',
  'responsible-user-not-found': 'responsibleUserNotFound',
  'responsible-agent-missing': 'responsibleAgentMissing',
  'responsible-agent-not-found': 'responsibleAgentNotFound',
  'responsible-agent-not-in-service': 'responsibleAgentNotInService',
  'item-description-required': 'itemDescriptionRequired',
  'workflow-state-unknown': 'workflowStateUnknown',
  'reposition-reason-missing': 'repositionReasonMissing',
  'item-changed-since-read': 'itemChangedSinceRead',
  'move-not-waiting': 'moveNotWaiting',
  'work-area-statuses-missing': 'workAreaStatusesMissing',
  'work-area-status-key-empty': 'workAreaStatusKeyEmpty',
  'work-area-status-key-twice': 'workAreaStatusKeyTwice',
  'work-area-status-name-missing': 'workAreaStatusNameMissing',
  'item-type-unknown': 'itemTypeUnknown',
  sign_in_link_gone: 'signInLinkGone',
  email_required: 'guestAddressRequired',
  invitation_needs_sign_in: 'invitationNeedsSignIn',
  'package-version-not-published': 'packageVersionNotPublished',
  'package-ships-with-server': 'packageShipsWithServer',
  'package-not-installed': 'packageNotInstalled',
  'package-not-published': 'packageNotPublished',
  'organisations-unknown': 'organisationsUnknown',
  'package-linked-copy-changed': 'packageLinkedCopyChanged',
  'deployment-link-origin-unset': 'deploymentLinkOriginUnset',
  'deployment-link-code-invalid': 'deploymentLinkCodeInvalid',
  'deployment-link-not-found': 'deploymentLinkNotFound',
  'deployment-link-not-active': 'deploymentLinkNotActive',
  'package-merge-nothing': 'packageMergeNothing',
  'package-merge-incomplete': 'packageMergeIncomplete',
  'package-page-changed-since-preview': 'packagePageChangedSincePreview',
  'package-merge-not-a-page': 'packageMergeNotAPage',
  'deployment-invitation-not-a-request': 'deploymentInvitationNotARequest',
  'deployment-invitation-open': 'deploymentInvitationOpen',
  'deployment-invitation-none-open': 'deploymentInvitationNoneOpen',
  'deployment-invitation-account-exists': 'deploymentInvitationAccountExists',
  'owner-flow-idea-not-found': 'ownerFlowIdeaNotFound',
  'owner-flow-not-an-idea': 'ownerFlowNotAnIdea',
  'owner-flow-already-owns': 'ownerFlowAlreadyOwns',
  'owner-flow-not-an-owner': 'ownerFlowNotAnOwner',
  'owner-flow-owner-role-missing': 'ownerFlowOwnerRoleMissing',
  'owner-flow-lead-role-missing': 'ownerFlowLeadRoleMissing',
  'owner-flow-writer-role-missing': 'ownerFlowWriterRoleMissing',
  'owner-flow-cannot-be-told': 'ownerFlowCannotBeTold',
  'owner-flow-writer-agent-refused': 'ownerFlowWriterAgentRefused',
  'owner-flow-intake-owner-refused': 'ownerFlowIntakeOwnerRefused',
  'owner-flow-person-not-found': 'ownerFlowPersonNotFound',
  'owner-flow-role-not-found': 'ownerFlowRoleNotFound',
  'owner-flow-agent-not-found': 'ownerFlowAgentNotFound',
  'owner-flow-intake-unchangeable': 'ownerFlowIntakeUnchangeable',
  'responsible-agent-out-of-reach': 'responsibleAgentOutOfReach',
  'item-not-imported': 'itemNotImported',
  'work-area-status-in-use': 'workAreaStatusInUse',
  'next-up-config-not-found': 'nextUpConfigNotFound',
  'item-no-reopen-target': 'itemNoReopenTarget',
  'item-checks-waiting': 'itemChecksWaiting',
  'flow-answer-names-no-node': 'flowAnswerNamesNoNode',
  'flow-answer-not-offered': 'flowAnswerNotOffered',
  'flow-answer-names-no-approver': 'flowAnswerNamesNoApprover',
  'flow-plan-not-revision': 'flowPlanNotRevision',
  'flow-request-answered': 'flowRequestAnswered',
  'flow-waits-for-children': 'flowWaitsForChildren',
  'flow-generated-no-nodes': 'flowGeneratedNoNodes',
  'item-awaits-confirmation': 'itemAwaitsConfirmation',
  'step-person-unknown': 'stepPersonUnknown',
  'item-hand-over-refused': 'itemHandOverRefused',
  'item-type-stays-in-work-area': 'itemTypeStaysInWorkArea',
  'evidence-link-invalid': 'evidenceLinkInvalid',
  'evidence-file-not-attachment': 'evidenceFileNotAttachment',
  'item-flow-not-found': 'itemFlowNotFound',
  'flow-switch-nothing-named': 'flowSwitchNothingNamed',
  'flow-switch-too-many': 'flowSwitchTooMany',
  'flow-switch-other-work-area': 'flowSwitchOtherWorkArea',
  'item-moves-by-own-service': 'itemMovesByOwnService',
  'item-parts-changed': 'itemPartsChanged',
  'item-merge-other-work-area': 'itemMergeOtherWorkArea',
  'item-merge-no-cancelled-status': 'itemMergeNoCancelledStatus',
  'item-related-other-work-area': 'itemRelatedOtherWorkArea',
  'item-confirm-no-state': 'itemConfirmNoState',
  'work-area-cap-unreadable': 'workAreaCapUnreadable',
  'work-area-cap-no-status': 'workAreaCapNoStatus',
  'resolved-sha-not-configured': 'resolvedShaNotConfigured',
  'landed-sha-missing': 'landedShaMissing',
  'landed-sha-malformed': 'landedShaMalformed',
  'landed-sha-not-verifiable': 'landedShaNotVerifiable',
  'landed-sha-record-unreadable': 'landedShaRecordUnreadable',
  'landed-sha-ambiguous': 'landedShaAmbiguous',
  'landed-sha-not-recorded': 'landedShaNotRecorded',
  'agent-takes-persons-decision': 'agentTakesPersonsDecision',
  'work-area-address-unusable': 'workAreaAddressUnusable',
  'work-area-address-taken': 'workAreaAddressTaken',
  'work-area-address-held': 'workAreaAddressHeld',
  'public-place-not-found': 'publicPlaceNotFound',
  'group-more-visible-than-organisation': 'groupMoreVisibleThanOrganisation',
  'public-places-depend': 'publicPlacesDepend',
  'work-area-children-more-visible': 'workAreaChildrenMoreVisible',
  'work-area-parent-less-visible': 'workAreaParentLessVisible',
  'search-indexing-needs-public': 'searchIndexingNeedsPublic',
  'search-indexing-not-allowed': 'searchIndexingNotAllowed',
  'intake-responsible-refused': 'intakeResponsibleRefused',
  'agent-responsible-refused': 'agentResponsibleRefused',
  'own-agent-not-found': 'ownAgentNotFound',
  'work-area-agent-not-in-service': 'workAreaAgentNotInService',
  'work-area-type-default-inactive': 'workAreaTypeDefaultInactive',
  'work-area-type-not-found': 'workAreaTypeNotFound',
  'work-area-type-is-default': 'workAreaTypeIsDefault',
  'work-area-type-in-use': 'workAreaTypeInUse',
  'work-area-type-inactive': 'workAreaTypeInactive',
  'transfer-owner-only': 'transferOwnerOnly',
  'transfer-already-owner': 'transferAlreadyOwner',
  'transfer-needs-member': 'transferNeedsMember',
  'person-no-account': 'personNoAccount',
  'already-a-member': 'alreadyAMember',
  'work-area-member-not-found': 'workAreaMemberNotFound',
  'owner-role-fixed': 'ownerRoleFixed',
  'owner-not-removable': 'ownerNotRemovable',
  'hand-over-unavailable': 'handOverUnavailable',
  'hand-over-items-not-held': 'handOverItemsNotHeld',
  'hand-over-to-removed': 'handOverToRemoved',
  'work-area-own-parent': 'workAreaOwnParent',
  'work-area-hierarchy-cycle': 'workAreaHierarchyCycle',
  'desktop-sign-in-request-invalid': 'desktopSignInRequestInvalid',
  'agent-not-found': 'agentNotFound',
  'agent-key-missing': 'agentKeyMissing',
  'agent-name-missing': 'agentNameMissing',
  'agent-key-is-identifier': 'agentKeyIsIdentifier',
  'agent-key-taken': 'agentKeyTaken',
  'agent-key-ambiguous': 'agentKeyAmbiguous',
  'agent-access-unavailable': 'agentAccessUnavailable',
  'agent-merged-away': 'agentMergedAway',
  'agent-session-out-of-service': 'agentSessionOutOfService',
  'agent-write-out-of-service': 'agentWriteOutOfService',
  'agent-creation-off': 'agentCreationOff',
  'agent-creation-needs-approval': 'agentCreationNeedsApproval',
  'agent-create-needs-permission': 'agentCreateNeedsPermission',
  'agent-request-not-found': 'agentRequestNotFound',
  'agent-request-already-decided': 'agentRequestAlreadyDecided',
  'agent-request-reason-missing': 'agentRequestReasonMissing',
  'agent-request-reason-too-long': 'agentRequestReasonTooLong',
  'agent-handover-unavailable': 'agentHandoverUnavailable',
  'agent-handover-to-itself': 'agentHandoverToItself',
  'agent-merge-same': 'agentMergeSame',
  'agent-merge-target-not-active': 'agentMergeTargetNotActive',
  'agent-override-not-a-count': 'agentOverrideNotACount',
  'agent-override-needs-date': 'agentOverrideNeedsDate',
  'agent-override-date-invalid': 'agentOverrideDateInvalid',
  'agent-override-date-past': 'agentOverrideDatePast',
  'credential-names-its-agent': 'credentialNamesItsAgent',
  'view-act-not-attributable': 'viewActNotAttributable',
  'view-writes-as-you': 'viewWritesAsYou',
  'subagent-role-refuses': 'subagentRoleRefuses',
  'subagent-files-no-finished-item': 'subagentFilesNoFinishedItem',
  'subagent-fold-not-found': 'subagentFoldNotFound',
} as const satisfies Record<ReaderRefusalCodeType, string>;

export type ReaderRefusalKeyType = (typeof READER_REFUSAL_KEYS)[ReaderRefusalCodeType];

/** Where a permission was missing, as `permission-missing`'s sentence names the place. */
export type PermissionMissingWhereType = 'system' | 'namespace' | 'workArea' | 'thisWorkArea';

/** What the refused subject is told it can do: who grants it, or for an agent why activating won't. */
export type PermissionMissingRemedyType =
  | 'systemAdmin'
  | 'ownerOrManager'
  | 'agentConnection'
  | 'agentActive'
  | 'agentActivate';

/** `permission-missing`'s facts. `workArea` is the work area's name; `agent` and `workAreaId` are the activation command's. */
export interface IPermissionMissingArgs {
  readonly subject: 'user' | 'agent';
  readonly permission: string;
  readonly where: PermissionMissingWhereType;
  readonly workArea?: string;
  readonly remedy: PermissionMissingRemedyType;
  readonly agent?: string;
  readonly workAreaId?: string;
}

export type ReaderRefusalArgsType = Readonly<Record<string, string | number>>;

/**
 * The facts whose value changes the words around them, and the values that have words of their own;
 * any other value says `other`. Each becomes a part of its own, `failures.readerRefusalParts.<key>.<fact>.
 * <value>`, said first and passed into the sentence as that fact, so no sentence needs ICU `select`:
 * the web entry runs simple messages without the formatter (`shared/src/i18n/lite.ts`), and every
 * reader refusal is said there.
 */
export const READER_REFUSAL_CHOICES: Partial<
  Readonly<Record<ReaderRefusalKeyType, Readonly<Record<string, readonly string[]>>>>
> = {
  permissionMissing: {
    subject: ['agent'],
    where: ['system', 'namespace', 'workArea'],
    remedy: ['systemAdmin', 'agentConnection', 'agentActive', 'agentActivate'],
  },
  personOnlyAct: { act: ['ok', 'pin', 'approval', 'signOff'] },
  pageChangedElsewhere: { what: ['page', 'pagesUnder', 'pages'] },
  signedInSessionOnly: {
    act: ['mintBridge', 'mintAgent', 'signOutOthers', 'revokeUnused', 'confirmDesktopSignIn'],
  },
  verificationSessionOwnCredential: { action: ['revoke', 'rename'] },
  settingsChangedElsewhere: {
    settings: [
      'overview',
      'workAreaOrder',
      'toolbar',
      'openingTabs',
      'phone',
      'views',
      'notifications',
    ],
  },
  partRetiredFirst: { act: ['addUnder', 'split', 'merge', 'mergeInto'] },
  approvalAnswerMissing: { outcome: ['confirmed'] },
  answerRequestAmbiguous: { what: ['approval'] },
  invitationLinkNoAddress: { then: ['ask'] },
  invitationOtherAddress: { then: ['signUp'] },
  invitationOutOfRange: { unit: ['days'] },
  deploymentLinkNotActive: { state: ['invited', 'pending'] },
  responsibleAgentNotInService: { status: ['inactive'] },
  agentSessionOutOfService: { status: ['inactive'] },
  agentRequestAlreadyDecided: { status: ['approved', 'declined'] },
};

/** The part each choice fact says, by fact: `failures.readerRefusalParts.<key>.<fact>.<value or other>`. */
export function readerRefusalParts(
  key: ReaderRefusalKeyType,
  args: ReaderRefusalArgsType,
): Readonly<Record<string, string>> {
  const choices = READER_REFUSAL_CHOICES[key];
  if (!choices) return {};
  return Object.fromEntries(
    Object.entries(choices).map(([fact, values]) => {
      const value = String(args[fact] ?? '');
      return [
        fact,
        `failures.readerRefusalParts.${key}.${fact}.${values.includes(value) ? value : 'other'}`,
      ];
    }),
  );
}

/** The body a reader refusal answers with: Nest's own fields, the code, and its facts. */
export interface IReaderRefusalBody {
  readonly statusCode: number;
  readonly error: string;
  readonly code: ReaderRefusalCodeType;
  readonly message: string;
  readonly args: ReaderRefusalArgsType;
}

const CODES: ReadonlySet<string> = new Set(Object.values(READER_REFUSALS));

export function isReaderRefusalCode(code: unknown): code is ReaderRefusalCodeType {
  return typeof code === 'string' && CODES.has(code);
}

/**
 * A fact the server keeps in every language it has, as a package text does (`{ en, de }`): its words
 * for `locale`, else its English. Null for any other object. Read here rather than through
 * `package-text.ts`, whose catalogues the page's first screen does not carry.
 */
function textFactIn(value: unknown, locale: string): string | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null;
  const words = value as Record<string, unknown>;
  if (typeof words.en !== 'string') return null;
  const said = words[locale];
  return typeof said === 'string' && said.trim() !== '' ? said : words.en;
}

/**
 * The refusal a response body carries, or null when it is not one of these (or an older server's).
 * String and number facts are kept as they are; a fact kept in every language (`{ en, de }`) is said
 * in `locale`, else in English (IN-101). Any other object is left out: a fact is said inside a
 * sentence.
 */
export function readerRefusalOf(
  body: unknown,
  locale = 'en',
): {
  code: ReaderRefusalCodeType;
  key: ReaderRefusalKeyType;
  args: ReaderRefusalArgsType;
  parts: Readonly<Record<string, string>>;
} | null {
  const candidate = body as { code?: unknown; args?: unknown } | null;
  const code = candidate?.code;
  if (!isReaderRefusalCode(code)) return null;
  // A code with no sentence in this build is said as an unknown one: the server's English (IN-108).
  const key = READER_REFUSAL_KEYS[code];
  if (!key) return null;
  const args: Record<string, string | number> = {};
  if (candidate?.args && typeof candidate.args === 'object') {
    for (const [name, value] of Object.entries(candidate.args as Record<string, unknown>)) {
      if (typeof value === 'string' || typeof value === 'number') args[name] = value;
      else {
        const said = textFactIn(value, locale);
        if (said !== null) args[name] = said;
      }
    }
  }
  return { code, key, args, parts: readerRefusalParts(key, args) };
}
